import { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import Navbar from '../components/Navbar'
import Footer from '../components/Footer'
import '../styles/LandingPage.css'

interface FAQItem {
  q: string
  a: string
}

interface FAQCategory {
  category: string
  questions: FAQItem[]
}

function LandingPage() {
  const navigate = useNavigate()
  const { user, isAuthenticated, loading } = useAuth()
  const [openFAQ, setOpenFAQ] = useState<number | null>(null)
  const [visibleSections, setVisibleSections] = useState<Set<string>>(new Set())
  const [countersStarted, setCountersStarted] = useState<boolean>(false)
  const firstStepIconRef = useRef<HTMLDivElement>(null)
  const lastStepIconRef = useRef<HTMLDivElement>(null)
  const verticalLineRef = useRef<HTMLDivElement>(null)
  const hasRedirected = useRef(false)

  // Redirect authenticated users to their dashboard
  useEffect(() => {
    if (!loading && isAuthenticated && user && !hasRedirected.current) {
      hasRedirected.current = true
      
      let redirectPath = ''
      switch (user.role) {
        case 'PATIENT':
          redirectPath = '/patient/dashboard-new'
          break
        case 'PHARMACY_PARTNER':
          redirectPath = '/pharmacy/dashboard-new'
          break
        case 'COMMUNITY_PARTNER':
          redirectPath = '/community/dashboard-new'
          break
        case 'CAMPUS_AMBASSADOR':
          redirectPath = '/campus/dashboard-new'
          break
        case 'SUPER_ADMIN':
        case 'ADMIN':
        case 'SUPERADMIN':
        case 'PARTNER_ADMIN':
          redirectPath = '/admin/dashboard'
          break
        default:
          hasRedirected.current = false
          return
      }
      
      if (redirectPath) {
        setTimeout(() => navigate(redirectPath, { replace: true }), 0)
      }
    }
  }, [loading, isAuthenticated, user, navigate])

  // FAQ toggle handler
  const toggleFAQ = (index: number) => {
    setOpenFAQ(openFAQ === index ? null : index)
  }

  // Animated counters
  const animateCounters = useCallback(() => {
    // Counter animation logic (currently not displayed in UI)
    // This function is kept for potential future use
  }, [])

  // Calculate vertical line height dynamically for mobile
  useEffect(() => {
    const calculateLineHeight = () => {
      if (firstStepIconRef.current && lastStepIconRef.current && verticalLineRef.current) {
        const firstIconRect = firstStepIconRef.current.getBoundingClientRect()
        const lastIconRect = lastStepIconRef.current.getBoundingClientRect()
        const wrapperRect = verticalLineRef.current.parentElement?.getBoundingClientRect()

        if (wrapperRect) {
          // Calculate distance from first icon center to last icon center
          const firstIconCenter = firstIconRect.top - wrapperRect.top + firstIconRect.height / 2
          const lastIconCenter = lastIconRect.top - wrapperRect.top + lastIconRect.height / 2
          const lineHeight = lastIconCenter - firstIconCenter

          // Only apply on mobile (screen width <= 1023px)
          if (window.innerWidth <= 1023) {
            verticalLineRef.current.style.height = `${lineHeight}px`
          } else {
            verticalLineRef.current.style.height = ''
          }
        }
      }
    }

    // Wait a bit for animations to complete
    const timeout = setTimeout(calculateLineHeight, 300)
    window.addEventListener('resize', calculateLineHeight)

    return () => {
      window.removeEventListener('resize', calculateLineHeight)
      clearTimeout(timeout)
    }
  }, [visibleSections])

  // Intersection Observer for fade-in animations
  useEffect(() => {
    const sections = ['hero', 'services', 'process', 'why-choose', 'pricing', 'faq', 'cta-section']

    // Detect iOS
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)

    // Use lower threshold and rootMargin for iOS to trigger earlier
    const observerOptions = isIOS
      ? { threshold: 0.01, rootMargin: '100px 0px' } // Trigger almost immediately on iOS with larger margin
      : { threshold: 0.1 }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSections((prev) => new Set([...prev, entry.target.id]))

            // Start counters when pricing section is visible
            if ((entry.target.id === 'pricing' || entry.target.id === 'services') && !countersStarted) {
              setCountersStarted(true)
              animateCounters()
            }
          }
        })
      },
      observerOptions
    )

    // Observe all sections immediately
    const checkAndObserve = () => {
      sections.forEach((id) => {
        const element = document.getElementById(id)
        if (element) {
          observer.observe(element)
          // On iOS, also mark as visible immediately if already in viewport or close to it
          if (isIOS) {
            const rect = element.getBoundingClientRect()
            const isInViewport = rect.top < window.innerHeight + 200 && rect.bottom > -200
            if (isInViewport) {
              setVisibleSections((prev) => new Set([...prev, id]))
            }
          }
        }
      })
    }

    // Check immediately and after a short delay to catch any late-rendering elements
    checkAndObserve()
    const timeout = setTimeout(checkAndObserve, 100)

    return () => {
      observer.disconnect()
      clearTimeout(timeout)
    }
  }, [countersStarted, animateCounters])

  // FAQ data structure
  const faqData: FAQCategory[] = [
    {
      category: 'PrEP (HIV Prevention)',
      questions: [
        {
          q: 'What is PrEP?',
          a: 'PrEP (Pre-Exposure Prophylaxis) is a highly effective way to prevent HIV. It can be taken as a daily pill or as a long-acting injection given every 2 or 6 months, depending on what works best for you.'
        },
        {
          q: 'Who should take PrEP?',
          a: 'PrEP is recommended for anyone who wants added protection against HIV. It may be a good option if you\'re sexually active, have multiple partners, or simply want peace of mind. A licensed provider will help determine if PrEP is right for you.'
        },
        {
          q: 'What PrEP options are available?',
          a: 'PrEP is available as a once-daily pill or as long-acting injectable PrEP administered every 2 months or 6 months. During your telehealth visit, your provider will review your needs and help you choose the best option.'
        },
        {
          q: 'Are there side effects?',
          a: 'Most people who take PrEP experience few or no side effects. Some may experience mild side effects like nausea, headache, or fatigue, which usually go away within the first few weeks. Serious side effects are rare. Your provider will discuss potential side effects during your consultation.'
        }
      ]
    },
    {
      category: 'DoxyPEP & Getting Started',
      questions: [
        {
          q: 'Is DoxyPEP the same as PrEP?',
          a: 'No. PrEP prevents HIV and is taken either as a daily pill or as a long-acting injection every 2 or 6 months. DoxyPEP is a separate medication taken after sex to help reduce the risk of certain bacterial STIs, including syphilis, chlamydia, and gonorrhea.'
        },
        {
          q: 'What is DoxyPEP?',
          a: 'It is a \'morning after\' antibiotic proven to reduce bacterial STIs by up to 80%, taken within 72 hours after sex for maximum effectiveness.'
        },
        {
          q: 'How do I get started?',
          a: 'Getting started is easy. Complete our brief online questionnaire, get your lab work done at a nearby location or with an at-home kit, have a provider review your results, and receive your medication via discreet delivery. The entire process takes less than 10 minutes to start.'
        }
      ]
    }
  ]

  const handleGetStarted = () => navigate('/register')

  return (
    <div className="app">
      <Navbar />

      {/* Hero Section */}
      <section id="hero" className={`hero ${visibleSections.has('hero') ? 'fade-in' : ''}`}>
        <div className="hero-background">
          <div className="hero-bg-circle hero-bg-circle-1"></div>
          <div className="hero-bg-circle hero-bg-circle-2"></div>
          <div className="hero-bg-circle hero-bg-circle-3"></div>
        </div>
        <div className="hero-container">
          <div className="hero-content">
            <div className="hero-left">
              <div className="hero-badge">
                <span className="hero-badge-dot"></span>
                <span className="hero-badge-text">Telehealth Available 24/7</span>
              </div>
              <h1 className="hero-title">
                <span className="hero-title-line">Stop HIV</span>
                <span className="hero-title-highlight">Before It Starts</span>
              </h1>
              <p className="hero-subtitle">
                Get PrEP & DoxyPEP — fast, discreet, and <strong>$0 for most patients</strong>. No clinic waiting rooms. No awkward conversations.
              </p>
              <div className="hero-features">
                <div className="hero-feature-item">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="hero-check-icon">
                    <path d="M21.801 10A10 10 0 1 1 17 3.335"></path>
                    <path d="m9 11 3 3L22 4"></path>
                  </svg>
                  <span>FDA Approved</span>
                </div>
                <div className="hero-feature-item">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="hero-check-icon">
                    <path d="M21.801 10A10 10 0 1 1 17 3.335"></path>
                    <path d="m9 11 3 3L22 4"></path>
                  </svg>
                  <span>Licensed Providers</span>
                </div>
                <div className="hero-feature-item">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="hero-check-icon">
                    <path d="M21.801 10A10 10 0 1 1 17 3.335"></path>
                    <path d="m9 11 3 3L22 4"></path>
                  </svg>
                  <span>Discreet Delivery</span>
                </div>
              </div>
              <div className="hero-buttons">
                <button className="btn-hero-primary" onClick={handleGetStarted}>
                  Get Started Today
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="btn-hero-arrow">
                    <path d="M5 12h14"></path>
                    <path d="m12 5 7 7-7 7"></path>
                  </svg>
                </button>
                <button
                  className="btn-hero-secondary"
                  onClick={(e) => {
                    e.preventDefault()
                    const element = document.getElementById('services')
                    if (element) {
                      element.scrollIntoView({ behavior: 'smooth', block: 'start' })
                    }
                  }}
                >
                  Learn More
                </button>
              </div>
            </div>
            <div className="hero-right">
              <div className="hero-card-wrapper">
                <div className="hero-card">
                  <div className="hero-card-header">
                    <div className="hero-card-icon">
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"></path>
                      </svg>
                    </div>
                    <div>
                      <h3 className="hero-card-title">PrEP Stop</h3>
                      <p className="hero-card-subtitle">Your protection partner</p>
                    </div>
                  </div>
                  <div className="hero-card-stats">
                    <div className="hero-card-stat">
                      <p className="hero-card-stat-value">99%</p>
                      <p className="hero-card-stat-label">Effective when taken daily</p>
                    </div>
                    <div className="hero-card-stat hero-card-stat-blue">
                      <p className="hero-card-stat-value">$0</p>
                      <p className="hero-card-stat-label">For most patients</p>
                    </div>
                  </div>
                  <div className="hero-card-progress-section">
                    <div className="hero-card-progress-header">
                      <span className="hero-card-progress-label">Getting Started</span>
                      <span className="hero-card-progress-time">~5 min</span>
                    </div>
                    <div className="hero-card-progress-bar-container">
                      <div className="hero-card-progress-bar" style={{ width: '75%' }}></div>
                    </div>
                  </div>
                </div>
                <div className="hero-card-badge-top">
                  <div className="hero-card-badge-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21.801 10A10 10 0 1 1 17 3.335"></path>
                      <path d="m9 11 3 3L22 4"></path>
                    </svg>
                  </div>
                  <div>
                    <p className="hero-card-badge-title">Prescription Ready</p>
                    <p className="hero-card-badge-subtitle">Same-day approval</p>
                  </div>
                </div>
                <div className="hero-card-badge-bottom">
                  <p className="hero-card-badge-text" style={{ fontSize: '0.875rem', fontWeight: '600' }}>Provider-Led HIV Prevention</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Comprehensive Protection Section */}
      <section id="services" className={`comprehensive-protection ${visibleSections.has('services') ? 'fade-in' : ''}`}>
        <div className="section-divider"></div>
        <div className="comprehensive-protection-container">
          <div className="section-header">
            <span className="section-badge">Our Services</span>
            <h2 className="section-title">Comprehensive Protection</h2>
            <p className="section-subtitle">Two powerful options to keep you protected. Choose what works best for your lifestyle.</p>
          </div>
          <div className="protection-cards">
            <div className={`protection-card prep-card ${visibleSections.has('services') ? 'slide-in-left' : ''}`}>
              <div className="protection-card-top-border"></div>
              <div className="protection-card-content">
                <div className="protection-card-header">
                  <div>
                    <div className="protection-card-title-row">
                      <div className="protection-card-icon-wrapper prep-icon-wrapper">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="protection-card-icon prep-icon">
                          <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"></path>
                        </svg>
                      </div>
                      <div>
                        <h3 className="protection-card-title">PrEP</h3>
                        <p className="protection-card-subtitle">HIV Prevention</p>
                      </div>
                    </div>
                    <p className="protection-card-tag prep-tag">Protection, Your Way</p>
                  </div>
                </div>
                <p className="protection-card-description">
                  Pre-exposure prophylaxis (PrEP) is a daily pill that reduces the risk of getting HIV from sex by about 99% when taken consistently.
                </p>
                <div className="protection-card-list">
                  <div className="protection-card-list-item">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="protection-check-icon">
                      <path d="M21.801 10A10 10 0 1 1 17 3.335"></path>
                      <path d="m9 11 3 3L22 4"></path>
                    </svg>
                    <span>Up to 99% effective when taken daily</span>
                  </div>
                  <div className="protection-card-list-item">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="protection-check-icon">
                      <path d="M21.801 10A10 10 0 1 1 17 3.335"></path>
                      <path d="m9 11 3 3L22 4"></path>
                    </svg>
                    <span>Safe, FDA-approved medication</span>
                  </div>
                  <div className="protection-card-list-item">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="protection-check-icon">
                      <path d="M21.801 10A10 10 0 1 1 17 3.335"></path>
                      <path d="m9 11 3 3L22 4"></path>
                    </svg>
                    <span>Multiple options available (daily or on-demand)</span>
                  </div>
                  <div className="protection-card-list-item">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="protection-check-icon">
                      <path d="M21.801 10A10 10 0 1 1 17 3.335"></path>
                      <path d="m9 11 3 3L22 4"></path>
                    </svg>
                    <span>Free lab work and consults included</span>
                  </div>
                </div>
                <button className="protection-card-btn prep-btn" onClick={handleGetStarted}>
                  Get PrEP
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="protection-btn-arrow">
                    <path d="M5 12h14"></path>
                    <path d="m12 5 7 7-7 7"></path>
                  </svg>
                </button>
              </div>
            </div>
            <div className={`protection-card doxypep-card ${visibleSections.has('services') ? 'slide-in-right' : ''}`}>
              <div className="protection-card-top-border doxypep-border"></div>
              <div className="protection-card-content">
                <div className="protection-card-header">
                  <div>
                    <div className="protection-card-title-row">
                      <div className="protection-card-icon-wrapper doxypep-icon-wrapper">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="protection-card-icon doxypep-icon">
                          <path d="m10.5 20.5 10-10a4.95 4.95 0 1 0-7-7l-10 10a4.95 4.95 0 1 0 7 7Z"></path>
                          <path d="m8.5 8.5 7 7"></path>
                        </svg>
                      </div>
                      <div>
                        <h3 className="protection-card-title">DoxyPEP</h3>
                        <p className="protection-card-subtitle">STI Prevention</p>
                      </div>
                    </div>
                    <p className="protection-card-tag doxypep-tag">Protect After Sex</p>
                  </div>
                </div>
                <p className="protection-card-description">
                  Doxycycline post-exposure prophylaxis (DoxyPEP) is taken within 72 hours after sex to reduce the risk of bacterial STIs.
                </p>
                <div className="protection-card-list">
                  <div className="protection-card-list-item">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="protection-check-icon">
                      <path d="M21.801 10A10 10 0 1 1 17 3.335"></path>
                      <path d="m9 11 3 3L22 4"></path>
                    </svg>
                    <span>Helps reduce the risk of chlamydia, gonorrhea, and syphilis
                    </span>
                  </div>
                  <div className="protection-card-list-item">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="protection-check-icon">
                      <path d="M21.801 10A10 10 0 1 1 17 3.335"></path>
                      <path d="m9 11 3 3L22 4"></path>
                    </svg>
                    <span>Proven to lower bacterial STI risk by up to 88%</span>
                  </div>
                  <div className="protection-card-list-item">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="protection-check-icon">
                      <path d="M21.801 10A10 10 0 1 1 17 3.335"></path>
                      <path d="m9 11 3 3L22 4"></path>
                    </svg>
                    <span>Take only when needed, after sex</span>
                  </div>
                  <div className="protection-card-list-item">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="protection-check-icon">
                      <path d="M21.801 10A10 10 0 1 1 17 3.335"></path>
                      <path d="m9 11 3 3L22 4"></path>
                    </svg>
                    <span>Simple, one-time dose</span>
                  </div>
                </div>
                <button className="protection-card-btn doxypep-btn" onClick={handleGetStarted}>
                  Get DoxyPEP
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="protection-btn-arrow">
                    <path d="M5 12h14"></path>
                    <path d="m12 5 7 7-7 7"></path>
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Four Simple Steps */}
      <section id="process" className={`steps ${visibleSections.has('process') ? 'fade-in' : ''}`}>
        <div className="steps-container">
          <div className="section-header">
            <span className="section-badge">Simple Process</span>
            <h2 className="section-title">Four Simple Steps to Protection</h2>
            <p className="section-subtitle">From consultation to delivery, we've made getting protected as easy as possible.</p>
          </div>
          <div className="steps-grid-wrapper">
            <div className="steps-vertical-line" ref={verticalLineRef}></div>
            <div className="steps-grid">
              {[
                {
                  icon: (
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect width="8" height="4" x="8" y="2" rx="1" ry="1"></rect>
                      <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"></path>
                      <path d="M12 11h4"></path>
                      <path d="M12 16h4"></path>
                      <path d="M8 11h.01"></path>
                      <path d="M8 16h.01"></path>
                    </svg>
                  ),
                  color: 'blue',
                  title: 'Quick Questionnaire',
                  desc: 'Complete a brief, confidential health questionnaire online. Takes about 5 minutes from anywhere.'
                },
                {
                  icon: (
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="m16 13 5.223 3.482a.5.5 0 0 0 .777-.416V7.87a.5.5 0 0 0-.752-.432L16 10.5"></path>
                      <rect x="2" y="6" width="14" height="12" rx="2"></rect>
                    </svg>
                  ),
                  color: 'indigo',
                  title: 'HIV Testing',
                  desc: 'We coordinate your HIV test with one of our trusted partner locations to ensure fast, accurate results — no at-home kits or lab scheduling required.'
                },
                {
                  icon: (
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
                      <circle cx="9" cy="7" r="4"></circle>
                      <polyline points="16 11 18 13 22 9"></polyline>
                    </svg>
                  ),
                  color: 'violet',
                  title: 'Provider Review',
                  desc: 'A licensed healthcare provider reviews your results and prescribes your medication.'
                },
                {
                  icon: (
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M11 21.73a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73z"></path>
                      <path d="M12 22V12"></path>
                      <polyline points="3.29 7 12 12 20.71 7"></polyline>
                      <path d="m7.5 4.27 9 5.15"></path>
                    </svg>
                  ),
                  color: 'red',
                  title: 'Discreet Delivery or Care Coordination',
                  desc: 'Your PrEP is delivered discreetly to your door — or we coordinate a smooth, in-person visit for long-acting injectable PrEP.'
                }
              ].map((step, index) => (
                <div key={index} className="step-card-wrapper">
                  <div className="step-icon-section" ref={index === 0 ? firstStepIconRef : index === 3 ? lastStepIconRef : undefined}>
                    <div className={`step-icon-wrapper ${step.color}`}>
                      <div className={`step-icon ${step.color}`}>
                        {step.icon}
                      </div>
                    </div>
                  </div>
                  <div className={`step-card ${visibleSections.has('process') ? `step-reveal-${index + 1}` : ''}`}>
                    <div className={`step-label ${step.color}`}>STEP {String(index + 1).padStart(2, '0')}</div>
                    <h3>{step.title}</h3>
                    <p>{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="steps-cta">
            <p>Ready to get started? The process takes less than 10 minutes.</p>
            <button onClick={handleGetStarted}>
              Start Your Consultation
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3"></path>
              </svg>
            </button>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className={`why-choose ${visibleSections.has('why-choose') ? 'fade-in' : ''}`}>
        <div className="why-choose-container">
          <div className="section-header">
            <span className="section-badge why-badge">Why PrEP Stop</span>
            <h2 className="section-title">Why Choose Us?</h2>
            <p className="section-subtitle">We've reimagined sexual health care to be accessible, affordable, and judgment-free.</p>
          </div>
          <div className="why-choose-grid">
            {[
              {
                icon: (
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                  </svg>
                ),
                title: 'Free Labs & Consults',
                desc: 'All required lab work and medical consultations are included at no extra cost to you.',
                badge: 'Included'
              },
              {
                icon: (
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
                    <circle cx="9" cy="7" r="4"></circle>
                    <path d="M22 21v-2a4 4 0 0 0-3-3.87"></path>
                    <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                  </svg>
                ),
                title: 'Expert Clinical Team',
                desc: 'Our board-certified providers specialize in sexual health and HIV prevention.',
                badge: 'Specialists'
              },
              {
                icon: (
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="m10.5 20.5 10-10a4.95 4.95 0 1 0-7-7l-10 10a4.95 4.95 0 1 0 7 7Z"></path>
                    <path d="m8.5 8.5 7 7"></path>
                  </svg>
                ),
                title: 'Medication Management',
                desc: 'Automatic refills, reminders, and ongoing support to keep you protected.',
                badge: 'Automated'
              },
              {
                icon: (
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                    <polyline points="12 6 12 12 16 14"></polyline>
                  </svg>
                ),
                title: '24/7 Availability',
                desc: 'Start your consultation anytime. Our platform is available around the clock.',
                badge: 'Always Open'
              },
              {
                icon: (
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"></path>
                    <path d="m9 12 2 2 4-4"></path>
                  </svg>
                ),
                title: 'Complete Privacy',
                desc: 'HIPAA-compliant platform with discreet packaging and confidential care.',
                badge: 'Secure'
              },
              {
                icon: (
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect width="20" height="14" x="2" y="5" rx="2"></rect>
                    <line x1="2" x2="22" y1="10" y2="10"></line>
                  </svg>
                ),
                title: 'Insurance Accepted',
                desc: 'We work with most major insurance plans to maximize your coverage.',
                badge: 'Covered'
              }
            ].map((item, index) => (
              <div
                key={index}
                className={`why-card ${visibleSections.has('why-choose') ? `fade-in-delay-${index}` : ''}`}
              >
                {item.badge && <div className="why-card-badge">{item.badge}</div>}
                <div className="why-icon-wrapper">
                  {item.icon}
                </div>
                <h3>{item.title}</h3>
                <p>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing/Affordability Section */}
      <section id="pricing" className={`pricing ${visibleSections.has('pricing') ? 'fade-in' : ''}`}>
        <div className="pricing-container">
          <div className="section-header" style={{ background: 'transparent' }}>
            <span className="pricing-badge">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z"></path>
                <path d="M20 3v4"></path>
                <path d="M22 5h-4"></path>
                <path d="M4 17v2"></path>
                <path d="M5 18H3"></path>
              </svg>
              Transparent Pricing
            </span>
            <h2 className="pricing-title">Your Health Shouldn't Have a Price Tag</h2>
            <p className="pricing-subtitle">We believe sexual health is a right, not a privilege. That's why we work to make prevention accessible.</p>
          </div>
          <div className="pricing-cards">
            {[
              {
                price: '$0',
                priceNote: 'for most patients',
                title: 'With Insurance',
                desc: 'Most insurance plans cover PrEP with zero copay. We handle all the paperwork.',
                highlight: 'Most Common',
                isDark: false,
                benefits: [
                  'Zero copay for medications',
                  'Free lab work coverage',
                  'Free provider consultations',
                  'No hidden fees'
                ]
              },
              {
                price: '$0-50',
                priceNote: 'per month',
                title: 'Without Insurance',
                desc: 'No insurance? We\'ll help you access manufacturer assistance programs.',
                highlight: null,
                isDark: true,
                benefits: [
                  'Gilead Advancing Access program',
                  'Manufacturer copay cards',
                  'Sliding scale options',
                  'Assistance with enrollment'
                ]
              },
              {
                price: 'Free',
                priceNote: 'always included',
                title: 'Labs & Consults',
                desc: 'All required monitoring and consultations are included at no cost.',
                highlight: null,
                isDark: true,
                benefits: [
                  'Initial lab work included',
                  'Quarterly monitoring labs',
                  'Unlimited messaging',
                  'Provider consultations'
                ]
              }
            ].map((card, index) => (
              <div
                key={index}
                className={`pricing-card ${card.isDark ? 'pricing-card-dark' : ''} ${visibleSections.has('pricing') ? `slide-up-${index + 1}` : ''}`}
              >
                {card.highlight && <div className="pricing-card-highlight">{card.highlight}</div>}
                <div className="pricing-card-header">
                  <h3 className="pricing-card-title">{card.title}</h3>
                  <div className="pricing-card-price">
                    <span className="pricing-card-price-value">{card.price}</span>
                  </div>
                  <p className="pricing-card-price-note">{card.priceNote}</p>
                </div>
                <p className="pricing-card-description">{card.desc}</p>
                <ul className="pricing-card-benefits">
                  {card.benefits.map((benefit, bIndex) => (
                    <li key={bIndex}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21.801 10A10 10 0 1 1 17 3.335"></path>
                        <path d="m9 11 3 3L22 4"></path>
                      </svg>
                      <span>{benefit}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div className="pricing-cta">
            <button onClick={handleGetStarted}>
              Start Your PrEP Journey
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 12h14"></path>
                <path d="m12 5 7 7-7 7"></path>
              </svg>
            </button>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className={`faq ${visibleSections.has('faq') ? 'fade-in' : ''}`}>
        <div className="faq-container">
          <div className="faq-header">
            <span className="faq-badge">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path>
                <path d="M12 17h.01"></path>
              </svg>
              Got Questions?
            </span>
            <h2 className="section-title">Frequently Asked Questions</h2>
            <p className="section-subtitle">Everything you need to know about PrEP, DoxyPEP, and getting started with PrEP Stop.</p>
          </div>
          <div className="faq-content">
            {faqData.map((category, catIndex) => {
              const startIndex = faqData.slice(0, catIndex).reduce((sum, cat) => sum + cat.questions.length, 0)
              return (
                <div key={catIndex} className="faq-category">
                  <h3 className="faq-category-title">{category.category}</h3>
                  <div className="faq-list">
                    {category.questions.map((item, qIndex) => {
                      const currentIndex = startIndex + qIndex
                      return (
                        <div key={qIndex} className={`faq-item ${openFAQ === currentIndex ? 'open' : ''}`}>
                          <button className="faq-question" onClick={() => toggleFAQ(currentIndex)}>
                            <span>{item.q}</span>
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="faq-toggle">
                              <path d="m6 9 6 6 6-6"></path>
                            </svg>
                          </button>
                          <div className="faq-answer">
                            <p>{item.a}</p>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )
            })}
          </div>
          <div className="faq-support">
            <p>Still have questions? <a href="#chat" onClick={(e) => { e.preventDefault(); }}>Chat with our support team</a></p>
          </div>
        </div>
      </section>

      {/* Ready to Get Protected */}
      <section className={`cta-section ${visibleSections.has('cta-section') ? 'fade-in' : ''}`}>
        <div className="cta-container">
          <div className="cta-icons">
            <div className="cta-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"></path>
              </svg>
            </div>
            <div className="cta-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect width="18" height="11" x="3" y="11" rx="2" ry="2"></rect>
                <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
              </svg>
            </div>
            <div className="cta-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"></path>
              </svg>
            </div>
          </div>
          <h2 className="cta-title">Ready to Get Protected?</h2>
          <p className="cta-subtitle">
            PrEP Stop makes HIV and STI prevention simple — with licensed providers, trusted local partners, and discreet care designed around you. Fast, private, and affordable.          </p>
          <div className="cta-buttons">
            <button className="cta-button-primary" onClick={handleGetStarted}>
              Start PrEP Today!
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 12h14"></path>
                <path d="m12 5 7 7-7 7"></path>
              </svg>
            </button>
          </div>
          <div className="cta-trust">
            <div className="cta-trust-item">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"></path>
              </svg>
              <span>HIPAA Compliant</span>
            </div>
            <div className="cta-trust-item">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"></path>
              </svg>
              <span>Licensed Providers</span>
            </div>
            <div className="cta-trust-item">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"></path>
              </svg>
              <span>256-bit Encryption</span>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  )
}

export default LandingPage
